import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import AIChat from "../components/AIChat";
import AISuggestions from "../components/AISuggestions";
import { useFeatures } from "../context/FeatureContext";
import { toast } from "sonner";
import {
  FolderKanban,
  CheckCircle2,
  Clock,
  Users,
  Truck,
  Package,
  DollarSign,
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  FileText,
  Bot,
  MessageSquare,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Settings2,
  Eye,
  EyeOff,
  User,
  Cog,
  Wrench,
  CalendarClock,
  Trash2,
  Check,
  X,
  ClipboardCheck,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default stat card preferences
const DEFAULT_STAT_CARDS = {
  total_projects: true,
  task_completion: true,
  pending_materials: true,
  total_expenses: true,
  team_members: true,
  suppliers: true,
  pending_tasks: true,
};

// Available stat cards configuration
const STAT_CARD_CONFIG = [
  { key: "total_projects", label: "Total Projects", category: "primary" },
  { key: "task_completion", label: "Task Completion", category: "primary" },
  { key: "pending_materials", label: "Pending Materials", category: "primary" },
  { key: "total_expenses", label: "Total Expenses", category: "primary" },
  { key: "team_members", label: "Team Members", category: "secondary" },
  { key: "suppliers", label: "Suppliers", category: "secondary" },
  { key: "pending_tasks", label: "Pending Tasks", category: "secondary" },
];

const StatCard = ({ title, value, icon: Icon, description, color = "slate" }) => (
  <Card className="rounded-sm border-slate-200 hover:shadow-sm transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-semibold text-slate-900 mt-2 tracking-tight">
            {value}
          </p>
          {description && (
            <p className="text-xs text-slate-400 mt-1">{description}</p>
          )}
        </div>
        <div
          className={`p-2 rounded-sm ${
            color === "blue"
              ? "bg-blue-50 text-blue-600"
              : color === "green"
              ? "bg-emerald-50 text-emerald-600"
              : color === "amber"
              ? "bg-amber-50 text-amber-600"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [pendingReturnRequests, setPendingReturnRequests] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatExpanded, setAiChatExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("compact"); // "compact" or "full"
  const [currentUser, setCurrentUser] = useState(null);
  const [showOnlyMyBookings, setShowOnlyMyBookings] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    booking_date: "",
    start_time: "",
    end_time: "",
  });
  const [statCardPrefs, setStatCardPrefs] = useState(() => {
    const saved = localStorage.getItem("dashboardStatCards");
    return saved ? JSON.parse(saved) : DEFAULT_STAT_CARDS;
  });
  const [prefsSheetOpen, setPrefsSheetOpen] = useState(false);
  const { isModuleEnabled, isMenuItemEnabled } = useFeatures();
  const [pendingRentalReservations, setPendingRentalReservations] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [rentalEvents, setRentalEvents] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, lowStockRes, pendingPOsRes, aiStatusRes, bookingsRes, userRes, returnReqsRes, rentalResvRes, workersRes, rentalEventsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/inventory/low-stock`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/purchase-orders/pending-approval`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/ai/status`, { withCredentials: true }).catch(() => ({ data: { ai_enabled: false } })),
          axios.get(`${API}/service-bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/rental-return-requests/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/rentals/pending-approval`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/users/workers`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/rentals/calendar/events`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        setLowStockItems(lowStockRes.data || []);
        setPendingPOs(pendingPOsRes.data || []);
        setPendingReturnRequests(returnReqsRes.data || []);
        setPendingRentalReservations(rentalResvRes.data || []);
        setWorkers(workersRes.data || []);
        setRentalEvents(rentalEventsRes.data || []);
        setAiEnabled(aiStatusRes.data?.ai_enabled || false);
        setCurrentUser(userRes.data);
        
        // Filter upcoming bookings (today and future, scheduled, confirmed or pending)
        const today = new Date().toISOString().split('T')[0];
        const upcoming = (bookingsRes.data || [])
          .filter(b => b.booking_date >= today && ['confirmed', 'pending', 'scheduled'].includes(b.status))
          .sort((a, b) => a.booking_date.localeCompare(b.booking_date) || (a.time_slot?.start_time || "").localeCompare(b.time_slot?.start_time || ""))
          .slice(0, 20);
        setUpcomingBookings(upcoming);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Save stat card preferences to localStorage
  const updateStatCardPref = (key, enabled) => {
    const newPrefs = { ...statCardPrefs, [key]: enabled };
    setStatCardPrefs(newPrefs);
    localStorage.setItem("dashboardStatCards", JSON.stringify(newPrefs));
  };

  // Filter bookings for workers (show only their assigned bookings)
  const filteredBookings = useMemo(() => {
    if (!showOnlyMyBookings || currentUser?.role === "admin") {
      return upcomingBookings;
    }
    // Filter to show only bookings assigned to current user
    return upcomingBookings.filter(booking => 
      booking.assigned_workers?.some(w => w.user_id === currentUser?.user_id)
    );
  }, [upcomingBookings, showOnlyMyBookings, currentUser]);

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      case "in_progress":
        return "bg-blue-50 text-blue-700 ring-blue-600/20";
      case "on_hold":
        return "bg-amber-50 text-amber-700 ring-amber-600/20";
      default:
        return "bg-slate-50 text-slate-700 ring-slate-600/20";
    }
  };

  const getBookingStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-emerald-200 text-emerald-800";
      case "pending":
        return "bg-amber-200 text-amber-800";
      case "scheduled":
        return "bg-blue-200 text-blue-800";
      case "overdue":
        return "bg-red-200 text-red-800";
      case "completed":
        return "bg-slate-300 text-slate-700";
      case "cancelled":
        return "bg-slate-200 text-slate-500";
      default:
        return "bg-slate-200 text-slate-700";
    }
  };

  // Booking action handlers
  const handleAssignWorker = async () => {
    if (!selectedBooking || !selectedWorkerId) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${selectedBooking.booking_id}/assign-worker`,
        { worker_id: selectedWorkerId },
        { withCredentials: true }
      );
      toast.success("Technician assigned successfully");
      const worker = workers.find(w => w.user_id === selectedWorkerId);
      setSelectedBooking({
        ...selectedBooking,
        assigned_worker_id: selectedWorkerId,
        assigned_worker_name: worker?.name || "",
      });
      // Refresh bookings
      const bookingsRes = await axios.get(`${API}/service-bookings`, { withCredentials: true });
      const today = new Date().toISOString().split("T")[0];
      const upcoming = (bookingsRes.data || [])
        .filter(b => b.booking_date >= today && ['confirmed', 'pending', 'scheduled'].includes(b.status))
        .sort((a, b) => a.booking_date.localeCompare(b.booking_date));
      setUpcomingBookings(upcoming);
      setSelectedWorkerId("");
    } catch (error) {
      toast.error("Failed to assign technician");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBookingAction = async (action) => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      const statusMap = {
        confirm: "confirmed",
        cancel: "cancelled",
        complete: "completed",
      };
      
      await axios.put(
        `${BACKEND_URL}/api/service-bookings/${selectedBooking.booking_id}/${action === "confirm" ? "confirm" : "edit"}`,
        action === "confirm" ? {} : { status: statusMap[action] },
        { withCredentials: true }
      );
      
      toast.success(`Booking ${action === "confirm" ? "confirmed" : action === "cancel" ? "cancelled" : "completed"}`);
      setBookingDialogOpen(false);
      
      // Refresh bookings
      const bookingsRes = await axios.get(
        `${BACKEND_URL}/api/service-bookings/calendar?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`,
        { withCredentials: true }
      );
      setUpcomingBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedBooking || !rescheduleData.booking_date) return;
    setActionLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/service-bookings/${selectedBooking.booking_id}/reschedule`,
        {
          booking_date: rescheduleData.booking_date,
          time_slot: {
            start_time: rescheduleData.start_time || selectedBooking.time_slot?.start_time,
            end_time: rescheduleData.end_time || selectedBooking.time_slot?.end_time,
          },
        },
        { withCredentials: true }
      );
      
      toast.success("Booking rescheduled");
      setRescheduleDialogOpen(false);
      setBookingDialogOpen(false);
      
      // Refresh bookings
      const bookingsRes = await axios.get(
        `${BACKEND_URL}/api/service-bookings/calendar?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`,
        { withCredentials: true }
      );
      setUpcomingBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Reschedule failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBooking) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/service-bookings/${selectedBooking.booking_id}`,
        { withCredentials: true }
      );
      
      toast.success("Booking deleted");
      setDeleteDialogOpen(false);
      setBookingDialogOpen(false);
      
      // Refresh bookings
      const bookingsRes = await axios.get(
        `${BACKEND_URL}/api/service-bookings/calendar?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`,
        { withCredentials: true }
      );
      setUpcomingBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openRescheduleDialog = () => {
    setRescheduleData({
      booking_date: selectedBooking?.booking_date || "",
      start_time: selectedBooking?.time_slot?.start_time || "",
      end_time: selectedBooking?.time_slot?.end_time || "",
    });
    setRescheduleDialogOpen(true);
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return filteredBookings.filter(b => b.booking_date === dateStr);
  };

  const getRentalEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return rentalEvents.filter(e => e.date === dateStr);
  };

  const navigateMonth = (delta) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-sm"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Overview of your manufacturing projects
          </p>
        </div>
        <Sheet open={prefsSheetOpen} onOpenChange={setPrefsSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-sm">
              <Settings2 className="h-4 w-4 mr-2" />
              Customize
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Dashboard Preferences</SheetTitle>
              <SheetDescription>
                Choose which statistics to display on your dashboard
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Primary Stats</h4>
                <div className="space-y-3">
                  {STAT_CARD_CONFIG.filter(c => c.category === "primary").map(card => (
                    <div key={card.key} className="flex items-center justify-between">
                      <Label htmlFor={card.key} className="text-sm">{card.label}</Label>
                      <Switch
                        id={card.key}
                        checked={statCardPrefs[card.key]}
                        onCheckedChange={(checked) => updateStatCardPref(card.key, checked)}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Secondary Stats</h4>
                <div className="space-y-3">
                  {STAT_CARD_CONFIG.filter(c => c.category === "secondary").map(card => (
                    <div key={card.key} className="flex items-center justify-between">
                      <Label htmlFor={card.key} className="text-sm">{card.label}</Label>
                      <Switch
                        id={card.key}
                        checked={statCardPrefs[card.key]}
                        onCheckedChange={(checked) => updateStatCardPref(card.key, checked)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Pending Bookings Banner - Show only if there are pending bookings */}
      {isModuleEnabled("service_booking") && (() => {
        const pendingCount = upcomingBookings.filter(b => b.status === "pending").length;
        const urgentCount = upcomingBookings.filter(b => {
          if (b.status !== "pending") return false;
          const created = new Date(b.created_at);
          const now = new Date();
          return Math.floor((now - created) / (1000 * 60 * 60 * 24)) >= 7;
        }).length;
        
        if (pendingCount === 0) return null;
        
        return (
          <Link to="/pending-approvals" className="block mb-6">
            <Card className={`rounded-sm cursor-pointer transition-all hover:shadow-md ${urgentCount > 0 ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${urgentCount > 0 ? "bg-red-100" : "bg-amber-100"}`}>
                    <Clock className={`h-5 w-5 ${urgentCount > 0 ? "text-red-600" : "text-amber-600"}`} />
                  </div>
                  <div>
                    <p className={`font-semibold ${urgentCount > 0 ? "text-red-800" : "text-amber-800"}`}>
                      {pendingCount} Pending Booking{pendingCount > 1 ? "s" : ""} Awaiting Approval
                    </p>
                    <p className={`text-sm ${urgentCount > 0 ? "text-red-600" : "text-amber-600"}`}>
                      {urgentCount > 0 
                        ? `⚠️ ${urgentCount} booking${urgentCount > 1 ? "s" : ""} waiting for 7+ days`
                        : "Click to review and approve customer requests"}
                    </p>
                  </div>
                </div>
                <ArrowRight className={`h-5 w-5 ${urgentCount > 0 ? "text-red-500" : "text-amber-500"}`} />
              </CardContent>
            </Card>
          </Link>
        );
      })()}

      {/* Pending Return Requests Banner */}
      {pendingReturnRequests.length > 0 && (currentUser?.role === "admin" || currentUser?.role === "manager") && (
        <Link to="/rental-management" className="block mb-6">
          <Card className="rounded-sm cursor-pointer transition-all hover:shadow-md border-purple-300 bg-purple-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-purple-800">
                    {pendingReturnRequests.length} Return/Replacement Request{pendingReturnRequests.length > 1 ? "s" : ""} Pending
                  </p>
                  <p className="text-sm text-purple-600">
                    Click to review customer rental return requests
                  </p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-purple-500" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Pending Rental Reservations Banner */}
      {pendingRentalReservations.length > 0 && (currentUser?.role === "admin" || currentUser?.role === "manager") && (
        <Link to="/pending-approvals" className="block mb-6">
          <Card className="rounded-sm cursor-pointer transition-all hover:shadow-md border-blue-300 bg-blue-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-blue-800">
                    {pendingRentalReservations.length} Rental Reservation{pendingRentalReservations.length > 1 ? "s" : ""} Pending Approval
                  </p>
                  <p className="text-sm text-blue-600">
                    Click to review new rental requests
                  </p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-blue-500" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Service Calendar Section - FIRST and Prominent */}
      {isModuleEnabled("service_booking") && (
        <Card className="rounded-sm border-slate-200 mb-8">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-600" />
                <CardTitle className="text-lg font-semibold">Service Calendar</CardTitle>
                {currentUser?.role === "worker" && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {showOnlyMyBookings ? "My Bookings" : "All Bookings"}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Worker filter toggle */}
                {(currentUser?.role === "worker" || currentUser?.role === "manager") && (
                  <Button
                    variant={showOnlyMyBookings ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowOnlyMyBookings(!showOnlyMyBookings)}
                    className="text-xs"
                  >
                    {showOnlyMyBookings ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                    {showOnlyMyBookings ? "My Jobs" : "All Jobs"}
                  </Button>
                )}
                <Button
                  variant={calendarView === "compact" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCalendarView("compact")}
                >
                  Compact
                </Button>
                <Button
                  variant={calendarView === "full" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCalendarView("full")}
                >
                  Full
                </Button>
                <Link to="/service-calendar">
                  <Button variant="outline" size="sm">
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {calendarView === "compact" ? (
              /* Compact View - Upcoming Bookings & Rental Events List */
              <div className="space-y-3">
                {filteredBookings.length === 0 && rentalEvents.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{showOnlyMyBookings ? "No events assigned to you" : "No upcoming events"}</p>
                  </div>
                ) : (
                  <>
                    {filteredBookings.slice(0, 5).map((booking) => (
                      <div
                        key={booking.booking_id}
                        className="flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer border-l-4 border-l-emerald-500"
                        onClick={() => {
                          setSelectedBooking(booking);
                          setSelectedWorkerId(booking.assigned_worker_id || "");
                          setBookingDialogOpen(true);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{booking.customer_name || "Customer"}</p>
                            <p className="text-sm text-slate-500">
                              {booking.booking_date} at {booking.time_slot?.start_time || "TBD"} - {booking.service_name || "General Service"}
                            </p>
                            {booking.assigned_worker_name && (
                              <p className="text-xs text-slate-400 mt-0.5">
                                <User className="h-3 w-3 inline mr-1" />
                                {booking.assigned_worker_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={getBookingStatusColor(booking.status)}>
                          Service
                        </Badge>
                      </div>
                    ))}
                    {rentalEvents.slice(0, 5).map((evt) => (
                      <div
                        key={evt.rental_id}
                        className={`flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer border-l-4 ${
                          evt.type === "delivery" ? "border-l-blue-500" : "border-l-orange-500"
                        }`}
                        onClick={() => window.location.href = `/rental-job-card/${evt.rental_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                            evt.type === "delivery" ? "bg-blue-50" : "bg-orange-50"
                          }`}>
                            <Truck className={`h-5 w-5 ${evt.type === "delivery" ? "text-blue-600" : "text-orange-600"}`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {evt.customer_name}
                              {evt.is_replacement && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Replacement</span>}
                            </p>
                            <p className="text-sm text-slate-500">
                              {evt.date} - {evt.item_name} x{evt.quantity}
                            </p>
                            {evt.assigned_technician_name && (
                              <p className="text-xs text-slate-400 mt-0.5">
                                <User className="h-3 w-3 inline mr-1" />
                                {evt.assigned_technician_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={evt.type === "delivery" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}>
                          {evt.type === "delivery" ? "Delivery" : "Return"}
                        </Badge>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              /* Full Calendar View */
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="font-semibold text-slate-900">
                    {currentDate.toLocaleString("default", { month: "long", year: "numeric" })}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mb-3 text-xs">
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"></span> Service</div>
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block"></span> Delivery</div>
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-orange-500 inline-block"></span> Return</div>
                </div>
                <div className="grid grid-cols-7 gap-1 auto-rows-min">
                  {getDaysInMonth().map((day, index) => {
                    const dayBookings = getBookingsForDay(day);
                    const dayRentals = getRentalEventsForDay(day);
                    const hasEvents = dayBookings.length > 0 || dayRentals.length > 0;
                    return (
                      <div
                        key={`calendar-day-${index}`}
                        className={`min-h-[80px] p-1 border rounded-sm ${
                          day ? "border-slate-200" : "border-transparent"
                        } ${isToday(day) ? "bg-blue-50 border-blue-300" : ""} ${
                          hasEvents && !isToday(day) ? "bg-slate-50" : ""
                        }`}
                      >
                        {day && (
                          <>
                            <span className={`text-xs font-medium ${isToday(day) ? "text-blue-600" : "text-slate-600"}`}>
                              {day}
                            </span>
                            {hasEvents && (
                              <div className="mt-1 space-y-1">
                                {dayBookings.map((b, i) => (
                                  <div
                                    key={`svc-${i}`}
                                    data-testid={`calendar-service-${b.booking_id}`}
                                    className="text-xs leading-tight px-1 py-1 rounded bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200 border-l-[3px] border-emerald-500"
                                    onClick={() => {
                                      setSelectedBooking(b);
                                      setSelectedWorkerId(b.assigned_worker_id || "");
                                      setBookingDialogOpen(true);
                                    }}
                                    title={`Service: ${b.customer_name} - ${b.service_name || "Service"}`}
                                  >
                                    <div className="font-medium">{b.customer_name}</div>
                                    {b.time_slot?.start_time && (
                                      <div className="opacity-80">{b.time_slot.start_time} - {b.time_slot.end_time}</div>
                                    )}
                                    <div className="opacity-70">{b.assigned_worker_name || "Unassigned"}</div>
                                  </div>
                                ))}
                                {dayRentals.map((r, i) => (
                                  <div
                                    key={`rent-${i}`}
                                    data-testid={`calendar-rental-${r.rental_id}`}
                                    className={`text-xs leading-tight px-1 py-1 rounded cursor-pointer border-l-[3px] ${
                                      r.type === "delivery"
                                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-500"
                                        : "bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-500"
                                    }`}
                                    onClick={() => window.location.href = `/rental-job-card/${r.rental_id}`}
                                    title={`${r.type === "delivery" ? "Delivery" : "Return"}: ${r.customer_name} - ${r.item_name}${r.is_replacement ? " (Replacement)" : ""}`}
                                  >
                                    <div className="font-medium flex items-center gap-1">
                                      {r.type === "delivery" ? "Delivery" : "Return"}
                                      {r.is_replacement && <span className="text-[10px] bg-purple-200 text-purple-700 px-1 rounded">RPL</span>}
                                    </div>
                                    <div>{r.customer_name}</div>
                                    <div className="opacity-80">{r.item_name} x{r.quantity}</div>
                                    <div className="opacity-70">{r.assigned_technician_name || "Unassigned"}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-slide-up">
        {isMenuItemEnabled("projects") && statCardPrefs.total_projects && (
          <StatCard
            title="Total Projects"
            value={stats?.total_projects || 0}
            icon={FolderKanban}
            description={`${stats?.active_projects || 0} active`}
            color="blue"
          />
        )}
        {isMenuItemEnabled("projects") && statCardPrefs.task_completion && (
          <StatCard
            title="Task Completion"
            value={`${stats?.task_completion_rate || 0}%`}
            icon={CheckCircle2}
            description={`${stats?.completed_tasks || 0} of ${stats?.total_tasks || 0} tasks`}
            color="green"
          />
        )}
        {isMenuItemEnabled("materials") && statCardPrefs.pending_materials && (
          <StatCard
            title="Pending Materials"
            value={stats?.pending_materials || 0}
            icon={Package}
            description="Awaiting delivery"
            color="amber"
          />
        )}
        {isMenuItemEnabled("expenses") && statCardPrefs.total_expenses && (
          <StatCard
            title="Total Expenses"
            value={`R ${(stats?.total_expenses || 0).toLocaleString()}`}
            icon={DollarSign}
            description="Across all projects"
          />
        )}
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {isMenuItemEnabled("team") && statCardPrefs.team_members && (
          <Card className="rounded-sm border-slate-200">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-sm">
                <Users className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate-900">
                  {stats?.total_team || 0}
                </p>
                <p className="text-sm text-slate-500">Team Members</p>
              </div>
            </CardContent>
          </Card>
        )}
        {isMenuItemEnabled("suppliers") && statCardPrefs.suppliers && (
          <Card className="rounded-sm border-slate-200">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-sm">
                <Truck className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate-900">
                  {stats?.total_suppliers || 0}
                </p>
                <p className="text-sm text-slate-500">Suppliers</p>
              </div>
            </CardContent>
          </Card>
        )}
        {isMenuItemEnabled("projects") && statCardPrefs.pending_tasks && (
          <Card className="rounded-sm border-slate-200">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-slate-100 rounded-sm">
                <Clock className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-slate-900">
                  {stats?.pending_tasks || 0}
                </p>
                <p className="text-sm text-slate-500">Pending Tasks</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Alerts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Low Stock Alert */}
        {isMenuItemEnabled("inventory") && lowStockItems.length > 0 && (
          <Card className="rounded-sm border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-lg font-semibold text-amber-800">
                  Low Stock Alert
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-600 mb-3">
                {lowStockItems.length} item{lowStockItems.length > 1 ? "s" : ""} below minimum threshold
              </p>
              <div className="space-y-2 mb-4">
                {lowStockItems.slice(0, 3).map((item) => (
                  <div
                    key={item.item_id}
                    className="flex items-center justify-between p-2 bg-white rounded-sm"
                  >
                    <span className="text-sm font-medium text-slate-700">
                      {item.name}
                    </span>
                    <span className="text-sm text-amber-700">
                      {item.current_stock} / {item.min_threshold} {item.unit}
                    </span>
                  </div>
                ))}
              </div>
              <Link to="/inventory">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-sm border-amber-400 text-amber-700 hover:bg-amber-100"
                  data-testid="view-low-stock-btn"
                >
                  View All Low Stock Items
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Pending POs */}
        {isModuleEnabled("purchase_orders") && pendingPOs.length > 0 && (
          <Card className="rounded-sm border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-lg font-semibold text-blue-800">
                  Pending Approval
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-blue-600 mb-3">
                {pendingPOs.length} purchase order{pendingPOs.length > 1 ? "s" : ""} awaiting approval
              </p>
              <div className="space-y-2 mb-4">
                {pendingPOs.slice(0, 3).map((po) => (
                  <div
                    key={po.po_id}
                    className="flex items-center justify-between p-2 bg-white rounded-sm"
                  >
                    <span className="text-sm font-medium text-slate-700">
                      {po.po_number}
                    </span>
                    <span className="text-sm text-blue-700">
                      R {po.total_amount?.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <Link to="/purchase-orders">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-sm border-blue-400 text-blue-700 hover:bg-blue-100"
                  data-testid="view-pending-pos-btn"
                >
                  Review Purchase Orders
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {/* AI Section - Only shows when AI module is enabled */}
      {isModuleEnabled("ai_stock_controller") && aiEnabled && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* AI Suggestions */}
          <AISuggestions />
          
          {/* AI Chat Toggle */}
          {!showAIChat && (
            <Card
              className="rounded-sm border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setShowAIChat(true)}
              data-testid="open-ai-chat"
            >
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Bot className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-indigo-900">AI Stock Controller</h3>
                    <p className="text-sm text-indigo-600">
                      Ask questions about your inventory and get smart recommendations
                    </p>
                  </div>
                </div>
                <Button className="rounded-sm bg-indigo-600 hover:bg-indigo-700">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Start Chat
                </Button>
              </CardContent>
            </Card>
          )}
          
          {/* AI Chat Widget */}
          {showAIChat && (
            <AIChat
              onClose={() => setShowAIChat(false)}
              isExpanded={aiChatExpanded}
              onToggleExpand={() => setAiChatExpanded(!aiChatExpanded)}
            />
          )}
        </div>
      )}

      {/* Recent Projects */}
      {isMenuItemEnabled("projects") && (
        <Card className="rounded-sm border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              Recent Projects
            </CardTitle>
            <Link
              to="/projects"
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors"
              data-testid="view-all-projects"
            >
              View all
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {stats?.recent_projects?.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_projects.map((project, index) => (
                <Link
                  key={project.project_id}
                  to={`/projects/${project.project_id}`}
                  className={`flex items-center justify-between p-4 rounded-sm border border-slate-200 hover:bg-slate-50 transition-colors animate-slide-up`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`project-${project.project_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                      <FolderKanban className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {project.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {project.description || "No description"}
                      </p>
                    </div>
                  </div>
                  <Badge
                    className={`rounded-sm ${getStatusColor(project.status)}`}
                  >
                    {project.status?.replace("_", " ")}
                  </Badge>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderKanban className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No projects yet</p>
              <Link
                to="/projects"
                className="text-sm text-slate-900 font-medium hover:underline"
              >
                Create your first project
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Booking Details Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="sm:max-w-2xl w-full">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>
              {selectedBooking?.booking_date} at{" "}
              {selectedBooking?.time_slot?.start_time} -{" "}
              {selectedBooking?.time_slot?.end_time}
            </DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <Badge className={`rounded-sm ${getBookingStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="font-medium">{selectedBooking.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Cog className="h-4 w-4 text-slate-400" />
                  <span>{selectedBooking.installation_name || "Installation"}</span>
                </div>
                {selectedBooking.service_name && (
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-slate-400" />
                    <span>{selectedBooking.service_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span>{selectedBooking.booking_date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span>
                    {selectedBooking.time_slot?.start_time} -{" "}
                    {selectedBooking.time_slot?.end_time}
                  </span>
                </div>
                {selectedBooking.assigned_worker_name && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>Assigned: <strong>{selectedBooking.assigned_worker_name}</strong></span>
                  </div>
                )}
              </div>

              {/* Worker Assignment */}
              {!["completed", "cancelled"].includes(selectedBooking.status) && currentUser?.role !== "customer" && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Assign Technician</p>
                  <div className="flex gap-2">
                    <Select
                      value={selectedWorkerId}
                      onValueChange={setSelectedWorkerId}
                    >
                      <SelectTrigger className="rounded-sm flex-1" data-testid="dashboard-worker-select">
                        <SelectValue placeholder="Select technician" />
                      </SelectTrigger>
                      <SelectContent>
                        {workers.map((worker) => (
                          <SelectItem key={worker.user_id} value={worker.user_id}>
                            {worker.name} ({worker.role === "worker" ? "Technician" : "Manager"})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAssignWorker}
                      disabled={!selectedWorkerId || actionLoading}
                      className="rounded-sm"
                      size="sm"
                    >
                      {selectedBooking.assigned_worker_id ? "Reassign" : "Assign"}
                    </Button>
                  </div>
                </div>
              )}
              {selectedBooking.notes && (
                <div className="p-3 bg-slate-50 rounded-sm">
                  <p className="text-sm text-slate-600">{selectedBooking.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-col gap-2 sm:justify-start">
            {/* Management buttons for non-completed/cancelled bookings */}
            {selectedBooking && !["completed", "cancelled"].includes(selectedBooking.status) && (
              <div className="flex gap-2 w-full justify-start">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openRescheduleDialog}
                  disabled={actionLoading}
                  className="rounded-sm flex-1"
                >
                  <CalendarClock className="h-4 w-4 mr-1" />
                  Reschedule
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={actionLoading}
                  className="rounded-sm text-red-600 hover:text-red-700 flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            )}
            
            {/* Status action buttons */}
            {selectedBooking?.status === "pending" && (
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  onClick={() => handleBookingAction("cancel")}
                  disabled={actionLoading}
                  className="rounded-sm flex-1"
                >
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
                <Button
                  onClick={() => handleBookingAction("confirm")}
                  disabled={actionLoading}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 flex-1"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Confirm
                </Button>
              </div>
            )}
            {(selectedBooking?.status === "confirmed" || selectedBooking?.status === "scheduled") && (
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/job-card/${selectedBooking.booking_id}`)}
                  className="rounded-sm flex-1"
                >
                  <ClipboardCheck className="h-4 w-4 mr-1" />
                  Job Card
                </Button>
                <Button
                  onClick={() => handleBookingAction("complete")}
                  disabled={actionLoading}
                  className="rounded-sm bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  Complete
                </Button>
              </div>
            )}
            
            {/* View in Service Calendar link - separate centered row */}
            <div className="w-full flex justify-center pt-2 border-t border-slate-100 mt-2">
              <Link to="/service-calendar">
                <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
                  View in Service Calendar
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule Booking</DialogTitle>
            <DialogDescription>
              Select new date and time for this booking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Date</Label>
              <Input
                type="date"
                value={rescheduleData.booking_date}
                onChange={(e) => setRescheduleData({ ...rescheduleData, booking_date: e.target.value })}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={rescheduleData.start_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={rescheduleData.end_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, end_time: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Booking</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this booking? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
